import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { take } from 'rxjs/operators';
import { BillingService } from 'src/app/services/billing.service';
import { LoginService } from 'src/app/services/login.service';
import { PolicyService } from 'src/app/services/policy.service';
import { ScriptService } from 'src/app/services/script.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'upsc-auth0',
  templateUrl: './auth0.component.html',
  styleUrls: ['./auth0.component.scss']
})
export class Auth0Component implements OnInit {
  locale; //This will be in the format ONL expects
  auth0locale; //This will NOT be in the usual it-IT format, but in the way auth) is expecting it to be. Without the hyphen part. so just "it" for example

  //Scenarios:
  systemError = false;
  loading = true;
  accessDenied = false;
  accessPending = false;
  nullTokenClaims = false;

  constructor(
    private auth: AuthService,
    private loginService: LoginService,
    private userService: UserService,
    private route: ActivatedRoute,
    private scriptService: ScriptService,
    private billingService: BillingService,
    private router: Router,
    private policyService: PolicyService
  ) {
    this.determineLocale();
    this.readParams();
  }

  ngOnInit() {
    this.determineLoginState();
  }

  //Takes the user to the Auth0 login page, config is setup in environment file.
  loginWithRedirect() {
    if (this.auth0locale && this.auth0locale == 'en-CA') {
      this.auth.loginWithRedirect({
        authorizationParams: {
          ui_locales: this.auth0locale,  //Set the locale here as needed
          redirect_uri: environment.auth0.authorizationParams.redirect_uri,
          'ext-style': 'canada'
        }
      });
    } else {
      this.auth.loginWithRedirect({
        authorizationParams: {
          ui_locales: this.auth0locale,  //Set the locale here as needed
          redirect_uri: environment.auth0.authorizationParams.redirect_uri,
          'ext-lang': this.locale,
          acr_values: this.locale
        }
      });
    }
  }

  //auth0 sdk should revoke tokens, end session, and redirect back to login page
  logout() {
    this.auth.logout({
      logoutParams: {
        ui_locales: this.auth0locale,  //Set the locale here as needed
        returnTo: environment.auth0.defaultLogoutUrl,
        'ext-lang': this.locale,
        acr_values: this.locale
      }
    });
  }

  //Main function is to determine if a user is logged in and where to navigate them. Calls process token to help decide.
  determineLoginState() {
    const pirateShipLanding = sessionStorage.getItem('pirateShipLanding');
    const flowType = sessionStorage.getItem('flowType');
    if (pirateShipLanding) {  //encrypted
      window.location.replace(`${environment.baseUrl.web}pirate-ship`);
      return;
    } else if (flowType && flowType == 'pirateship') { //un-encrypted
      window.location.replace(`${environment.baseUrl.web}pirate-ship`);
      return;
    }
    this.accessDenied = false;
    this.accessPending = false;
    this.systemError = false;
    this.nullTokenClaims = false;

    this.auth.isAuthenticated$.pipe(take(1)).subscribe(
      (isAuthenticated: boolean) => {
        if (isAuthenticated) {
          //check for an idToken. If there is one, we are authenticated and have user claims.
          this.auth.idTokenClaims$.pipe(take(1)).subscribe(
            (claims: IdToken | null | undefined) => {
              if (claims && claims?.sub && claims?.sub?.startsWith('email|')) {
                //User most likely came from a guest flow, has a passwordless auth, but needs to login to their actual account now.
                this.logout();  //invalidate their passwordless token
                return;
              }
              if (claims) {
                this.loginService.processToken(claims.__raw).subscribe(
                  data => {
                    if (!data.policyNumber) {
                      //no policy# found, user is most likely not created in our UserInfo table. Attempt to create now.
                      this.loading = false;
                      this.handleCreateUserFlow(data);
                    } else if (!data.active) {
                      //account is inactive, most likely got deleted. Deny access
                      this.loading = false;
                      this.accessDenied = true;
                    } else if ((!data.permissions) || (data.permissions.length <= 0) || (!data.approved)) {
                      //account has no permission, or is not approved yet by an admin user. deny access
                      this.loading = false;
                      this.accessPending = true;
                    } else {
                      //User has a policy number in our user info table, proceed to next steps
                      this.gatherPolicyData(data);
                    }
                  }, error => {
                    this.logout();
                  }
                );
              } else {
                //***Claims are null. SDK concluded that isAuthenticated==false***
                //- ID-token validation may have failed after the redirect to ONL.
                //- Computer clock may be off, making the token appear expired or not yet valid.
                //- Possible Nonce mismatch
                //- Issuer/Audience/Authorized-Party/Organization mismatch
                //- Missing or Invalid 'sub' 'exp' 'iat' or unsupported signing algorithm.
                //- Auth0 could not persist the ID token to localStorage (only if localStorage is enabled in app.module.ts) 'QuotaExceededError' or 'SecurityError'
                //- The token response reached Auth0 successfully, but the browser connection was interrupted before processing the response.
                //- Another tab/login transaction interfered with the current callback.
                //- Something cleared Auth0's cached entries immediately after they were written.
                this.nullTokenClaims = true;
                this.loading = false;
              }
            }, error => {
              //Possible meaning:
              //Could not reach Auth0 APIs
              //SDK threw an error while reading localStorage
              //SDK threw an error while checking authentication state
              //SDK threw an error while reading/decoding ID-token claims
              console.log('error-inner: ' + error);
              this.systemError = true;
              this.loading = false;
            }
          );
        } else {
          this.loginWithRedirect();
        }
      }, error => {
        console.log('error: ' + error);
        this.loginWithRedirect();
      }
    );
  }

  //Parse and determine what to do based on URL parameters
  readParams() {
    let error = this.route?.snapshot?.queryParams?.error;
    if (error) {
      this.systemError = true;
    } else {
      this.systemError = false;
    }
  }

  determineLocale() {
    this.userService.getLocale().subscribe(
      locale => {
        if (locale && locale.toLowerCase() != 'en-ca') {
          this.auth0locale = locale.split('-')[0]; //Get only the language part
          this.locale = locale;
        } else if (locale && locale.toLowerCase() == 'en-ca') {
          this.auth0locale = 'en-CA';  //They do want hyphen part for en-CA. You can find exactly what they are looking for each language when going to Autho0 homepage, and the Settings in the bottom left corner. Tenant Settings, and then at the very bottom of those are the language options.
          this.locale = locale;
        } else {
          this.auth0locale = 'en'; //Default to English
          this.locale = 'en';
        }
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }, error => {
        this.auth0locale = 'en'; //Default to English
        this.locale = 'en';
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }
    );
  }

  gatherPolicyData(userDetailsFromDecodedToken) {
    this.userService.getPolicySearch(userDetailsFromDecodedToken.policyNumber).subscribe(
      policy => {
        if (policy.code != 200) {
          this.systemError = true;
          this.loading = false;
          return;
        }
        sessionStorage.setItem('policyDetails', JSON.stringify(policy.data));
        this.gatherWalletData(policy, userDetailsFromDecodedToken);
      }, error => {
        this.systemError = true;
        this.loading = false;
      }
    );
  }

  gatherWalletData(policy, userDetailsFromDecodedToken) {
    let dwRequest = {
      productSourceSystem: "gw",
      sourceSystemIdentifierName: "PolicyNumber",
      sourceSystemIdentifier: policy?.data?.policyNumber,
      country: "US",
      roleType: "string",
      productType: "string"
    }

    this.billingService.DWgetSummary(dwRequest).subscribe(
      wallet => {
        if (wallet?.data?.wallet) {
          sessionStorage.setItem('dwDetails', JSON.stringify(wallet.data.wallet));
        }
        this.navigateToLoggedInExperience(policy, userDetailsFromDecodedToken);
      }, error => {
        this.navigateToLoggedInExperience(policy, userDetailsFromDecodedToken);
      }
    );
  }

  navigateToLoggedInExperience(policy, userDetailsFromDecodedToken) {
    this.scriptService.pendoInit();
    //Set session data, service data
    userDetailsFromDecodedToken.policyNumber = userDetailsFromDecodedToken.policyNumber.toUpperCase();  //Raise to uppercase P in case policy number came in lower case. Needed because other functions/apis may fail if we send lower case P
    sessionStorage.setItem('userDetails', JSON.stringify(userDetailsFromDecodedToken));
    this.userService.setUserInfo(userDetailsFromDecodedToken);
    this.userService.setUserPermissions(userDetailsFromDecodedToken.permissions);

    //First we handle weird scenario of HK Smart Upload policies
    if (policy?.data?.ref1?.toUpperCase() == 'HK') {
      this.policyService.getSmartUploadUrl({ policyno: policy.data.policyNumber, username: this.userService.getUserInfo().firstName }).subscribe(
        response => {
          if (response?.url && response.url != '') {
            window.location.href = response.url;
          } else {
            window.location.href = environment.smartUpload.url;
          }
        }, error => {
          window.location.href = environment.smartUpload.url;
        }
      );
    }

    //Was not HK SU case, so find the appropriate returnurl;
    //Set returnURL
    let returnURL = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    if (this.userService.isUserAdjuster()) {
      returnURL = '/adjusterDashboard'
    } else if (policy.data.policyType.key == 'Flex' && policy?.data?.policySource?.toUpperCase() == 'ISHIPFLEXMC') { // Tupss flex USPS policies check
      returnURL = '/billing';
    } else if (sessionStorage.getItem('returnUrl')) {
      returnURL = sessionStorage.getItem('returnUrl');
    }

    this.router.navigateByUrl(returnURL);
  }

  handleCreateUserFlow(userDetailsFromDecodedToken) {
    if (!userDetailsFromDecodedToken.emailAddress) {
      this.systemError = true;
      return;
    }
    this.userService.setUserInfo(userDetailsFromDecodedToken); //store the baseline token details, as it has the user's email.
    //Make sure to attach locale if present.
    if (this.locale && this.locale != 'en') {
      this.router.navigate([`create-account/${this.locale}`]);
    } else {
      this.router.navigate(['create-account'])
    }
  }

}
